import { useState } from "react"
import {
  useTableSortUrl,
  type TableQueryParams,
} from "@/v2-backoffice-client/components/table"
import SignInTable from "@/v2-backoffice-client/signIn/SignInTable"
import SignInPeriodSelect from "@/v2-backoffice-client/signIn/SignInPeriodSelect"
import { useSignInActivities } from "@/v2-backoffice-client/signIn/useSignInActivities"
import {
  deriveFilters,
  type SignInFilters,
} from "@/v2-backoffice-client/signIn/signIn.filters"
import type { Period } from "@/v2-backoffice-client/signIn/signIn.period"

const TABLE_ID = "sign-in-page-table"

export type SignInEventsTabProps = {
  period: Period
  onPeriodChange(value: Period): void
}

// The raw sign-in event list. Reads the durable store (paged) for every period
// and owns the server-side filter state — manualFiltering is always on so
// filters/search span the whole window, not just the loaded page.
function SignInEventsTab(props: SignInEventsTabProps) {
  const { period, onPeriodChange } = props

  const [filters, setFilters] = useState<SignInFilters>({})
  const { activities, loading, error, refetch, pageInfo, fetchMore } =
    useSignInActivities(period, filters)

  // Bookmarkable sort for this Activity tab only — SignInTable is also used
  // scoped inside the user view sheet, which stays URL-free.
  const sortUrl = useTableSortUrl({ tableId: TABLE_ID })

  function handleQueryChange(params: TableQueryParams) {
    const next = deriveFilters(params)
    setFilters((prev) =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
    )
  }

  return (
    <SignInTable
      activities={activities}
      isLoading={loading}
      error={error}
      showUser
      showSharingRisk={false}
      tableId={TABLE_ID}
      {...sortUrl}
      exportFileName="sign-ins"
      onRefresh={refetch}
      pageInfo={pageInfo}
      onFetchMore={fetchMore}
      manualFiltering
      searchColumn="Search"
      onQueryChange={handleQueryChange}
      toolbarExtra={
        <SignInPeriodSelect value={period} onChange={onPeriodChange} />
      }
    />
  )
}

export default SignInEventsTab
